import { Download, FolderOpen, GitBranch, Layout, Plus, Save, Trash2, Upload } from 'lucide-react';
import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useToast } from '../../components/ui/toast';
import { Input } from '../../components/ui/input';
import { toDesignNode, toDesignEdge, DESIGN_STORAGE_KEY } from '../../lib/designUtils';
import { generateTerraform } from '../../lib/terraform/generate';
import { importFromAzure } from '../../lib/designer/azureImport';
import { exportToGitHub, buildTerraformFiles } from '../../lib/designer/gitExport';
import { generateTfvars, generateVariables } from '../../lib/designer/environments';
import { fetchDesigns, saveDesignToServer, deleteDesignFromServer } from '../../lib/designer/designApi';

function loadDesignsFromStorage() {
  try {
    const stored = JSON.parse(localStorage.getItem(DESIGN_STORAGE_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
}

function saveDesignsToStorage(designs) {
  try {
    localStorage.setItem(DESIGN_STORAGE_KEY, JSON.stringify(designs));
  } catch {
    // Storage write failed
  }
}

function Toolbar({
  nodes,
  edges,
  designName,
  currentDesignId,
  onDesignNameChange,
  onNew,
  onLoad,
  onSaved,
  onOpenTemplates,
  onImportNodes,
}) {
  const toast = useToast();
  const { instance } = useMsal();
  const [saving, setSaving] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [subId, setSubId] = useState('');
  const [gitOpen, setGitOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [gitToken, setGitToken] = useState('');
  const [gitRepo, setGitRepo] = useState('');
  const [gitBranch, setGitBranch] = useState('terraform');

  async function handleSave() {
    if (!designName.trim()) {
      toast.error('Enter a design name first');
      return;
    }
    setSaving(true);
    const body = {
      name: designName,
      nodes: nodes.map(toDesignNode),
      edges: edges.map(toDesignEdge),
    };
    if (currentDesignId) {
      body.id = currentDesignId;
    }
    try {
      const saved = await saveDesignToServer(body);
      onSaved(saved.id);
      toast.success(currentDesignId ? 'Design saved' : 'Design created');
    } catch {
      const allDesigns = loadDesignsFromStorage();
      if (currentDesignId) {
        const idx = allDesigns.findIndex((d) => d.id === currentDesignId);
        if (idx >= 0) {
          allDesigns[idx] = { ...allDesigns[idx], ...body, updatedAt: new Date().toISOString() };
          saveDesignsToStorage(allDesigns);
          toast.success('Design saved (offline)');
        }
      } else {
        const newDesign = {
          id: Date.now().toString(36) + Math.random().toString(36).slice(2),
          ...body,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        allDesigns.push(newDesign);
        saveDesignsToStorage(allDesigns);
        onSaved(newDesign.id);
        toast.success('Design created (offline)');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleOpenLoad() {
    setLoadOpen(true);
    setLoadingList(true);
    try {
      const serverDesigns = await fetchDesigns();
      if (serverDesigns.length > 0) {
        setDesigns(serverDesigns.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
      } else {
        const allDesigns = loadDesignsFromStorage();
        setDesigns(allDesigns.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
      }
    } catch {
      const allDesigns = loadDesignsFromStorage();
      setDesigns(allDesigns.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
    } finally {
      setLoadingList(false);
    }
  }

  function handleLoadDesign(d) {
    onLoad(d);
    setLoadOpen(false);
    toast.success(`Loaded "${d.name}"`);
  }

  async function handleDelete(id, name) {
    try {
      await deleteDesignFromServer(id);
    } catch (_) {
      void 0;
    }
    const allDesigns = loadDesignsFromStorage();
    const updated = allDesigns.filter((d) => d.id !== id);
    saveDesignsToStorage(updated);
    setDesigns((prev) => prev.filter((d) => d.id !== id));
    toast.success(`Deleted "${name}"`);
  }

  async function handleAzureImport() {
    if (!subId.trim()) {
      toast.error('Enter a subscription ID');
      return;
    }
    setImporting(true);
    try {
      const account = instance.getActiveAccount();
      if (!account) {
        toast.error('Not authenticated');
        return;
      }
      const response = await instance.acquireTokenSilent({
        scopes: ['https://management.azure.com/user_impersonation'],
        account,
      });
      const result = await importFromAzure(response.accessToken, subId.trim());
      if (result.errors.length > 0) {
        toast.error(result.errors[0]);
      } else if (result.nodes.length > 0) {
        onImportNodes(result.nodes, result.edges);
        setImportOpen(false);
        toast.success(`Imported ${result.nodes.length} resources from Azure`);
      } else {
        toast.error('No resources found in subscription');
      }
    } catch {
      toast.error('Failed to import from Azure — check permissions');
    } finally {
      setImporting(false);
    }
  }

  async function handleGitExport() {
    const [owner, repo] = gitRepo.split('/');
    if (!owner || !repo) {
      toast.error('Enter repo as owner/repo');
      return;
    }
    setExporting(true);
    try {
      const designNodes = nodes.map(toDesignNode);
      const designEdges = edges.map(toDesignEdge);
      const result = generateTerraform(designNodes, designEdges);
      if (!result.ok) {
        toast.error('Cannot export: fix validation errors first');
        return;
      }
      const tfvars = generateTfvars(nodes, 'dev');
      const variables = generateVariables(nodes);
      const files = buildTerraformFiles(result.hcl, tfvars, variables);
      const res = await exportToGitHub(gitToken, owner, repo, gitBranch, files, `terraform: ${designName}`);
      setGitOpen(false);
      toast.success(`Pushed to GitHub: ${res.url}`);
    } catch (e) {
      toast.error(`GitHub export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  }

  function handleExport() {
    const designNodes = nodes.map(toDesignNode);
    const designEdges = edges.map(toDesignEdge);
    const result = generateTerraform(designNodes, designEdges);
    if (!result.ok) {
      toast.error(`Cannot export: ${result.errors[0] || 'Validation error'}`);
      return;
    }
    const blob = new Blob([result.hcl], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${designName.replace(/\s+/g, '-').toLowerCase() || 'design'}.tf`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Terraform file downloaded');
  }

  const outlineBtnClass =
    'h-7 px-2.5 text-xs border border-gray-300 dark:border-gray-600 bg-transparent text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors';
  const primaryBtnClass =
    'h-7 px-2.5 text-xs bg-teal-500 text-white rounded hover:bg-teal-600 disabled:opacity-50 flex items-center gap-1.5 transition-colors';

  return (
    <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2">
      <div className="flex items-center gap-1.5">
        <div className="h-3 w-3 rounded-sm bg-teal-500" />
        <span className="text-sm font-bold tracking-tight text-gray-900 dark:text-gray-100">
          CloudForge
        </span>
      </div>

      <div className="mx-2 h-4 w-px bg-gray-200 dark:bg-gray-700" />

      <Input
        value={designName}
        onChange={(e) => onDesignNameChange(e.target.value)}
        placeholder="Untitled design"
        className="h-7 w-44 text-sm"
      />

      <div className="mx-1 h-4 w-px bg-gray-200 dark:bg-gray-700" />

      <button type="button" className={outlineBtnClass} onClick={onNew}>
        <Plus className="h-3.5 w-3.5" />
        New
      </button>

      <button type="button" className={outlineBtnClass} onClick={onOpenTemplates}>
        <Layout className="h-3.5 w-3.5" />
        Templates
      </button>

      <button type="button" className={outlineBtnClass} onClick={handleOpenLoad}>
        <FolderOpen className="h-3.5 w-3.5" />
        Load
      </button>

      {loadOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setLoadOpen(false)}
        >
          <div
            className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Load Design
            </h2>
            {loadingList ? (
              <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                Loading…
              </p>
            ) : designs.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No saved designs yet
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {designs.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center justify-between rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2"
                  >
                    <button
                      type="button"
                      className="flex-1 text-left text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-teal-600 dark:hover:text-teal-400"
                      onClick={() => handleLoadDesign(d)}
                    >
                      {d.name}
                      <span className="ml-2 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                        {new Date(d.updatedAt).toLocaleDateString()}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center justify-center transition-colors"
                      onClick={() => handleDelete(d.id, d.name)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        className={primaryBtnClass}
        onClick={handleSave}
        disabled={saving}
      >
        <Save className="h-3.5 w-3.5" />
        {saving ? 'Saving…' : 'Save'}
      </button>

      <button type="button" className={outlineBtnClass} onClick={handleExport}>
        <Download className="h-3.5 w-3.5" />
        Export .tf
      </button>

      <div className="mx-1 h-4 w-px bg-gray-200 dark:bg-gray-700" />

      <button type="button" className={outlineBtnClass} onClick={() => setImportOpen(true)}>
        <Upload className="h-3.5 w-3.5" />
        Import
      </button>

      <button type="button" className={outlineBtnClass} onClick={() => setGitOpen(true)}>
        <GitBranch className="h-3.5 w-3.5" />
        Push to Git
      </button>

      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setImportOpen(false)}>
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Import from Azure</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Uses your Azure AD token to fetch existing resources from your subscription.
            </p>
            <Input
              value={subId}
              onChange={(e) => setSubId(e.target.value)}
              placeholder="Subscription ID (e.g. 00000000-0000-0000-0000-000000000000)"
              className="h-9 text-sm mb-3 font-mono"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setImportOpen(false)} className={outlineBtnClass}>Cancel</button>
              <button type="button" onClick={handleAzureImport} disabled={importing} className={primaryBtnClass}>
                {importing ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {gitOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setGitOpen(false)}>
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Push to GitHub</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Generates main.tf, variables.tf, and terraform.tfvars and commits them to a GitHub branch.
            </p>
            <Input
              value={gitToken}
              onChange={(e) => setGitToken(e.target.value)}
              placeholder="GitHub Personal Access Token"
              type="password"
              className="h-9 text-sm mb-2 font-mono"
            />
            <Input
              value={gitRepo}
              onChange={(e) => setGitRepo(e.target.value)}
              placeholder="owner/repo (e.g. offprem-cloud/terraform)"
              className="h-9 text-sm mb-2 font-mono"
            />
            <Input
              value={gitBranch}
              onChange={(e) => setGitBranch(e.target.value)}
              placeholder="Branch name"
              className="h-9 text-sm mb-3 font-mono"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setGitOpen(false)} className={outlineBtnClass}>Cancel</button>
              <button type="button" onClick={handleGitExport} disabled={exporting} className={primaryBtnClass}>
                {exporting ? 'Pushing…' : 'Push'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ml-auto">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {nodes.length} node{nodes.length !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
}

export default Toolbar;
